import {
  GridCustomCellProps,
  GridCustomHeaderCellProps,
} from "@progress/kendo-react-grid";
import { HeaderThElement, setGroupIds } from "@progress/kendo-react-data-tools";
import { Link, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { forEach } from 'lodash';
import { arrowUpIcon, arrowDownIcon } from "@progress/kendo-svg-icons";
import { SvgIcon } from '@progress/kendo-react-common';
import useIncidentTypes from "../../hooks/useIncidentTypes";
import { stages } from "../../utils/helpers/incidentFormTabs";
import { IncidentStatus } from "../../types/fieldInstance.interface";
import { useStoreWrapper, useViewStateWrapper } from "../../store/store";
import { buildIncidentOriginState } from "../../utils/helpers/incidentNavigation";

interface CustomGridCellProps extends GridCustomCellProps {
  cells?: {
    data: React.ComponentType<GridCustomCellProps>;
    append?: string;
  };
}
// Custom cell for the incidentId column to render a link
export const IncidentLinkCell = (props: GridCustomCellProps & { onRowClick?: (item: any) => void } & { icon?: React.ReactNode; useIconAsLink?: boolean }) => {
  const { dataItem, tdProps, onRowClick, useIconAsLink } = props;
  // Check multiple field names for the internal ID used in navigation (Salesforce ID like a02VD00000NDvzZYAT)
  // Order: camelCase first (transformed data), then PascalCase variants, then Salesforce Id
  const incidentId = dataItem.incidentId || dataItem.IncidentId || dataItem.IncidentID || dataItem.Id || dataItem.id;
  // Check multiple field names for the display value (incident number like INC-00013964)
  const incidentName = dataItem.IncidentName || dataItem.IncidentNumber || dataItem.incidentNumber || dataItem.incidentId || dataItem.IncidentId || "-";
  const rawData = dataItem.rawData;
  
  // Hooks
  const { incidentTypes } = useIncidentTypes();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, createForm, updateForm, setFromDraft } = useStoreWrapper(
    (state) => ({
      user: state.user,
      createForm: state.createForm,
      updateForm: state.updateForm,
      setFromDraft: state.setFromDraft,
    })
  );
  
  // If there's a custom click handler and raw data (for drafts), use it
  if (dataItem.status === IncidentStatus.Draft) {
    return (
      <td {...tdProps} style={{ display: "flex", justifyContent: "center" }}>
        <span
          onClick={() => {
            const incidentType = incidentTypes.find(
              (incidentType: any) => incidentType.flag === dataItem.recordTypeId
            );
            const incidentTypeId = incidentType ? incidentType.id : "";
            const savedStage = stages.find(
              (stage) => stage.label === dataItem.stageSavedAt
            );

            createForm(dataItem.id);
            updateForm(dataItem.id, {
              type: "UPDATE_FORM_STATE",
              payload: { incidentStatus: IncidentStatus.Draft },
            });
            updateForm(dataItem.id, {
              type: "UPDATE_BASE_FORM_DATA",
              payload: {
                incidentType: incidentTypeId,
                idempotencyKey: dataItem.idempotencyKey,
                facilityId: dataItem.facilityId,
              },
            });
            setFromDraft(true);
            const isOwner = dataItem.createdById === user.userId;
            const path = `/incidents/${dataItem.id}/${
              savedStage?.path ?? stages[0].path
            }?mode=${isOwner ? "edit" : "view"}`;
            useViewStateWrapper.getState().markLeavingForIncident();
            navigate(path, { state: buildIncidentOriginState(location) });
          }}
          style={{ textAlign: "center", cursor: "pointer" }}
        >
          {props.icon}
        </span>
      </td>
    );
  }
  // Default behavior for non-draft incidents
  return (
    <td {...tdProps} style={{ ...tdProps?.style, cursor: "pointer" }}>
      <Link
        to={`/incidents/${incidentId}/patient-demographics?mode=view`}
        state={buildIncidentOriginState(location)}
        onClick={() => useViewStateWrapper.getState().markLeavingForIncident()}
      >
        {useIconAsLink && props.icon ? props.icon : incidentName}
      </Link>
    </td>
  );
};

export const SortableDateCell = (props: GridCustomCellProps) => {
  const { dataItem, tdProps } = props;

  const dateString = dataItem.FacilityDate;
  const timeString = dataItem.FacilityTime;
  const combined = moment(`${dateString} ${timeString}`);

  return (
    <td {...tdProps}>
      {combined.isValid() ? combined.format("MM/DD/YYYY") : "-"}
    </td>
  );
};

export const CenteredWholeNumCell = (props: CustomGridCellProps) => {
  const { dataItem, tdProps } = props;

  let displayValue = 0;
  forEach(dataItem, (value, key) => {
    if (key === props.field) {
      displayValue = value;
      if (typeof value === "number" && !Number.isInteger(value)) {
        displayValue = Math.round(value);
      }
    }
  });

  return (
    <td {...tdProps}>
      <span style={{ display: "block", textAlign: "center" }}>
        {displayValue}
        {props.cells?.append ? props.cells.append : ""}
      </span>
    </td>
  );
};

export const HeaderCenteredCell = (props: GridCustomHeaderCellProps) => (
  <HeaderThElement
    columnId={props.thProps?.columnId || ""}
    {...props.thProps}
    style={{
      display: "grid-table",
    }}
    className="header-centered"
  >
    {props.children}
  </HeaderThElement>
);

export const TrendCell = (props: CustomGridCellProps & { previousMonthField?: string }) => {
  const { dataItem, field, previousMonthField } = props;
  const currentValue = dataItem[field || ''] || 0;
  const previousValue = previousMonthField ? (dataItem[previousMonthField] || 0) : null;

  let arrow = null;
  if (previousValue !== null) {
      if (currentValue < previousValue) {
          // Decrease in incidents -> Good -> Green Down Arrow
          arrow = <SvgIcon style={{ color: '#008000', marginLeft: '5px' }} icon={arrowDownIcon} size="medium" />;
      } else if (currentValue > previousValue) {
          // Increase in incidents -> Bad -> Red Up Arrow
          arrow = <SvgIcon style={{ color: '#ff0000', marginLeft: '5px' }} icon={arrowUpIcon} size="medium" />;
      }
  }

  return (
      <td {...props.tdProps} style={{ textAlign: 'left' }}>
          {currentValue}
          {arrow}
      </td>
  );
};

// Highlights a role-holder's name when they're one of the currently-selected
// caregivers (Caregiver Audit breakdown, MARCC-773/788). Matches by the paired
// "<field>Id" property on the row (Contact_on_Facility__c Id) — never by name,
// since names can collide.
export const MatchedCaregiverCell = (props: CustomGridCellProps & { selectedIds?: Set<string> }) => {
  const { dataItem, field, tdProps, selectedIds } = props;
  const name = field ? dataItem[field] : null;
  const id = field ? dataItem[`${field}Id`] : null;
  // Roles that can hold several caregivers (Last Provided Care — a multi-select over
  // Care_Provided__c children) send a "<field>People" [{name, id}] list, since one
  // "<field>Id" can't express a set. Highlight each selected caregiver individually.
  const people: { name: string; id: string }[] | undefined = field
    ? dataItem[`${field}People`]
    : undefined;
  const highlight = { color: "#b580ff", fontWeight: 600 } as const;

  if (Array.isArray(people)) {
    if (!people.length) return <td {...tdProps}>-</td>;
    return (
      <td {...tdProps}>
        {people.map((p, i) => (
          <span key={p.id ?? i}>
            {i > 0 && ", "}
            <span style={selectedIds?.has(p.id) ? highlight : undefined}>{p.name}</span>
          </span>
        ))}
      </td>
    );
  }

  const isMatch = !!id && !!selectedIds?.has(id);
  return (
    <td {...tdProps}>
      {name ? (
        <span style={isMatch ? highlight : undefined}>{name}</span>
      ) : (
        "-"
      )}
    </td>
  );
};
